package org.moxultimate.topwithmox.utils

import kotlinx.browser.window

/**
 * Analytics
 * Thin wrapper over Google Analytics 4 custom events, using the global gtag defined in AppEntry.
 */

/**
 * trackEvent
 * Sends a custom GA4 event. Does nothing when gtag is not loaded (blocked, offline, or not ready),
 * and never throws, so tracking can never break a click handler.
 * The current path is attached to every event as page_path.
 *
 * @param name GA4 event name, e.g. dashboard_card_click.
 * @param params Extra event parameters. String values only.
 */
fun trackEvent(name: String, params: Map<String, String> = emptyMap()) {
    try {
        val gtag = window.asDynamic().gtag
        if (jsTypeOf(gtag) != "function") return
        val payload: dynamic = js("({})")
        payload["page_path"] = window.location.pathname
        params.forEach { (key, value) -> payload[key] = value }
        window.asDynamic().gtag("event", name, payload)
    } catch (_: Throwable) {
    }
}

/**
 * trackDashboardCardClick
 * Records a click on a dashboard section card.
 *
 * @param section Dashboard section the card belongs to: wallpapers, games_apps, shows, tech or products.
 * @param title Card title shown to the user.
 * @param clickUrl Link the card opens; blank for cards that navigate inside the site.
 */
fun trackDashboardCardClick(section: String, title: String, clickUrl: String = "") {
    val params = mutableMapOf("section" to section, "title" to title)
    if (clickUrl.isNotBlank()) params["link_url"] = clickUrl
    trackEvent("dashboard_card_click", params)
}

/**
 * trackWallpaperDownload
 * Records a click on the wallpaper details Download button.
 *
 * @param title Wallpaper title.
 * @param type Wallpaper type: Phone, Desktop, Widescreen or Vertical.
 * @param series Series the wallpaper belongs to.
 */
fun trackWallpaperDownload(title: String, type: String, series: String) {
    trackEvent("wallpaper_download", mapOf("title" to title, "type" to type, "series" to series))
}

/**
 * trackOpen360View
 * Records a click on the Open 360 View button.
 *
 * @param title Wallpaper title.
 * @param type Wallpaper type: Phone, Desktop, Widescreen or Vertical.
 * @param series Series the wallpaper belongs to.
 */
fun trackOpen360View(title: String, type: String, series: String) {
    trackEvent("open_360_view", mapOf("title" to title, "type" to type, "series" to series))
}

/**
 * trackFilterChipClick
 * Records a click on a filter chip.
 *
 * @param label Chip text, such as Phone or a series name.
 * @param wasSelected Whether the chip was selected before the click; a click then deselects it.
 */
fun trackFilterChipClick(label: String, wasSelected: Boolean) {
    trackEvent("filter_chip_click", mapOf("label" to label, "action" to if (wasSelected) "deselect" else "select"))
}
